import { useRef } from 'react';
import { Anchor, Divider, Flex, HoverCard, ScrollArea, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { CustomFieldRowStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/CustomFieldRow.styles.tsx';
import { useIsTextClamped } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/CustomFieldsCard/useIsTextClamped.ts';

interface Props {
	label: string;
	value?: string | null;
}

const OVERLAY_MAX_HEIGHT = 260;
// The theme's shadow scale holds bare lengths, so Mantine's `shadow` prop renders none.
const OVERLAY_SHADOW = '0px 4px 12px 0px rgba(25, 28, 29, 0.16)';

export const CustomFieldRow = ({ label, value }: Props) => {
	const tMipManage = useTranslationMipManage();
	const rowRef = useRef<HTMLDivElement>(null);
	const labelRef = useRef<HTMLParagraphElement>(null);
	const valueRef = useRef<HTMLParagraphElement>(null);
	const displayValue = value || LONG_DASH;
	const isClamped = useIsTextClamped([labelRef, valueRef], `${label}\n${displayValue}`);

	return (
		<CustomFieldRowStyles ref={rowRef}>
			<div className="content">
				<Text ref={labelRef} className="label" fz={14} c="dark.0" lineClamp={2}>
					{label}
				</Text>

				<Text ref={valueRef} className="value" fz={14} fw={600} c="dark.8" lineClamp={2}>
					{displayValue}
				</Text>
			</div>

			{isClamped && (
				<HoverCard
					position="bottom-start"
					withinPortal
					styles={{ dropdown: { boxShadow: OVERLAY_SHADOW } }}
					width={rowRef.current?.offsetWidth}
					openDelay={100}
					closeDelay={150}
				>
					<HoverCard.Target>
						<Anchor
							component="button"
							type="button"
							fz={12}
							underline="always"
							w="fit-content"
						>
							{tMipManage('investors.details.custom_fields.view_more')}
						</Anchor>
					</HoverCard.Target>

					<HoverCard.Dropdown p={0}>
						<ScrollArea.Autosize mah={OVERLAY_MAX_HEIGHT} type="auto">
							<Flex direction="column" gap="0.5rem" p="0.75rem">
								<Flex direction="column" gap="0.25rem">
									<Text fz={12} fw={600} c="gray.9">
										{tMipManage('investors.details.custom_fields.question')}
									</Text>

									<Text fz={14} c="dark.0">
										{label}
									</Text>
								</Flex>

								<Divider />

								<Flex direction="column" gap="0.25rem">
									<Text fz={12} fw={600} c="gray.9">
										{tMipManage('investors.details.custom_fields.answer')}
									</Text>

									<Text fz={14} fw={600} c="dark.0">
										{displayValue}
									</Text>
								</Flex>
							</Flex>
						</ScrollArea.Autosize>
					</HoverCard.Dropdown>
				</HoverCard>
			)}
		</CustomFieldRowStyles>
	);
};
